import { Component, OnInit, ViewChild, Inject, AfterViewInit, OnDestroy, ElementRef } from '@angular/core';
import { Observable, BehaviorSubject, Subscription, of, Subject, merge } from 'rxjs';
import { map, distinctUntilChanged, tap, catchError, filter, concatMap } from 'rxjs/operators';
import { PayCustomerItemStatus } from '../../../shared/enums/smarthub-enums';
import { GridDataSource } from '../../../shared/models/grid-data-source';
import { UIPaymentService } from '../../../services/ui-payment.service';
import { UserNotification, UserNotificationCommand } from '../../../shared/components/notification-bar/notification-bar.model';
import * as _ from 'lodash';
import { ScrollDispatcher } from '@angular/cdk/scrolling';
import { SortEventData } from './ignite/web-components/sort-event';
import { PaginatorPageEvent, PaginatorPageEventRange } from './ignite/web-components/paginator-page-event';
import { UkgPaginator } from '@ignite/angular';
import { PayInfoFilters } from '../../pay-info/pay-info-filters/pay-info-filters.model';
import { DurationModel, PayRefundStatus } from '../stale-dated-check-view.model';
import * as moment from 'moment';
import { UltiCurrencyPipe } from '../../../shared/pipes/ulticurrency.pipe';
import { EnumTranslatePipe } from '../../../shared/pipes/enumtranslate.pipe';
import { FileExportService } from '../../../shared/services/export-file/export-file.service';
import { SettingsService } from '../../../shared/services/settings/settings.service';
import { TranslateService } from '@ngx-translate/core';
import { StaleCheckFiltersService } from '../stale-dated-check-info-filters/stale-dated-check-filters.service';
import { StaleDatedCheckFilters } from '../stale-dated-check-info-filters/stale-dated-check-filters.model';

class ExportedPayItem {

  static headers = {
    displayName: 'Name (Last, First)',
    employeeNumber: 'Employee ID',
    payGroup: 'Pay Group',
    payDate: 'Pay Date',
    checkNumber: 'Check #',
    checkAmount: 'Check Amount',
    staleDate: 'Stale Date',
    status : 'Status',
    refundDate: 'Refund Date',
    refundStatus: 'Refund Status'
  };

  static dateFormat = 'MM/DD/YYYY';

  static footer = {
    displayName: '* Printed on ' + moment().format(ExportedPayItem.dateFormat)
  };

  constructor(
    public displayName: string,
    public employeeNumber: string,
    public payGroup: string,
    public payDate: string,
    public checkNumber: string,
    public checkAmount: string,
    public staleDate: string,
    public status: string,
    public refundDate: string,
    public refundStatus: string) { }

  }

export class DisplayStaleDatedChecksInfo {
  id: string;
  firstName: string;
  lastName: string;
  displayName: string;
  employeeNumber: string;
  payGroup: string;
  payUtcDate: Date;
  checkNumber: string;
  checkAmount: number;
  staleDate: Date;
  status: PayCustomerItemStatus;
  refundDate?: Date;
  refundStatus: PayRefundStatus;
}

@Component({
  selector: 'ssg-stale-dated-checks-info-table',
  templateUrl: './stale-dated-checks-info-table.component.html',
  styleUrls: ['./stale-dated-checks-info-table.component.less']
})
export class StaleDatedChecksInfoTableComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly maxRecordCount = 10000;

  dataSource: GridDataSource<DisplayStaleDatedChecksInfo>;

  rawData: DisplayStaleDatedChecksInfo[];
  private _rawDataTable: { [id: string]: DisplayStaleDatedChecksInfo };
  private _selectedStaleCheckTable: { [id: string]: boolean };
  private _selectedStaleCheckItemCount: number;
  private _cachedSelection: DisplayStaleDatedChecksInfo[];

  private _sortSubject: BehaviorSubject<SortEventData>;
  private _pageSubject: BehaviorSubject<PaginatorPageEventRange>;
  selectedStaleDatedChecks: DisplayStaleDatedChecksInfo[];

  private _notification: UserNotification = null;
  private _manualFilterUpdate: Subject<any>;
  allColumns: string[];
  showFilters = false;
  sortKey: SortEventData;

  records: DisplayStaleDatedChecksInfo[];
  public initialrowsPerPage = 25 as number;
  public rowsOptions = [10, 25, 50, 100];
  private _dataSubscription: Subscription;

  @ViewChild('staleDatedChecksInfoTableNotificationBar', { static: true, read: ElementRef }) notificationBarElement: ElementRef;

  @ViewChild(UkgPaginator)
  private _paginator: UkgPaginator;

  get selectedStaleCheckItems(): DisplayStaleDatedChecksInfo[] {
    if (!this._cachedSelection) {
      const results: DisplayStaleDatedChecksInfo[] = [];
      for (const id in this._selectedStaleCheckTable) {
        if (this._selectedStaleCheckTable.hasOwnProperty(id) && this._selectedStaleCheckTable[id]) {
          const item = this._rawDataTable[id];
          if (item) {
            results.push(item);
          }
        }
      }

      this._cachedSelection = results;
    }

    return this._cachedSelection;
  }

  get selectedStaleCheckItemsCount(): number {
    return this._selectedStaleCheckItemCount;
  }

  get dataCount(): number {
    return (this.rawData || []).length;
  }

  userMessages = {
    applyFilter: { message: 'STALE_DATED_CHECKS.APPLY_FILTER',
      type: 'user-info',
      severity: 'information',
      persistToStorage: true,
      distinct: true },
    moreRecords: { message: 'STALE_DATED_CHECKS.MORE_RECORDS', type: 'status', severity: 'warning' },
    noMatchingRecords: { message: 'STALE_DATED_CHECKS.NO_MATCHING_RECORDS', type: 'status', severity: 'information' },
    success: { message: 'STALE_DATED_CHECKS.SUCCESSFUL_QUERY', type: 'status', severity: 'success' },
  };

  notificationSubject: Subject<UserNotificationCommand> = new Subject<UserNotificationCommand>();

  private _setStatusNotification(notif: string, params: any = null) {
    if (this._notification == null) {
      this._notification = new UserNotification(notif, params);
    }
  }

  private _publishNotification(notif: UserNotification): void {
    this.notificationSubject.next(new UserNotificationCommand('add', notif));
  }

  private _clearStatusNotification(): void {
    this.notificationSubject.next(
      new UserNotificationCommand('clearKey', this._notification)
    );
    this._notification = null;
  }
  // Sorting Event in table
  handleSort(newKey: CustomEvent<SortEventData>): void {
    this._sortSubject.next((newKey as CustomEvent<SortEventData>).detail);
  }

  constructor(
    @Inject(UIPaymentService) private readonly _uiPaymentService: UIPaymentService,
    @Inject(ScrollDispatcher) private readonly _scrollDispatcher: ScrollDispatcher,
    @Inject(UltiCurrencyPipe) private readonly _currencyPipe: UltiCurrencyPipe,
    @Inject(EnumTranslatePipe) private readonly _enumPipe: EnumTranslatePipe,
    @Inject(FileExportService) private readonly _fileExportService: FileExportService,
    @Inject(TranslateService) private readonly _translateService: TranslateService,
    @Inject(StaleCheckFiltersService) private readonly _filtersService: StaleCheckFiltersService,
    private readonly settings: SettingsService) { }

  ngOnInit() {
    this.allColumns = [
      'paySelected',
      'displayName',
      'employeeNumber',
      'payGroup',
      'payUtcDate',
      'checkNumber',
      'checkAmount',
      'staleDate',
      'status',
      'refundDate',
      'refundStatus'
    ];

    this.records = [];
    this._rawDataTable = {};
    this._resetSelection();

    // (filters + API data -> this._setRawData)
    const apiDataPipeline = this._initApiDataPipeline();

    this._sortSubject = new BehaviorSubject<SortEventData>({ key: null, isAsc: 'none' });
    const sortPipeline = this._sortSubject.pipe(
      distinctUntilChanged((previous: SortEventData, current: SortEventData) =>
        previous.key === current.key && previous.isAsc === current.isAsc));

    this._pageSubject = new BehaviorSubject<PaginatorPageEventRange>({ start: 0, end: this.initialrowsPerPage - 1 });
    const pagePipeline = this._pageSubject.pipe(
      distinctUntilChanged((previous: PaginatorPageEventRange, current: PaginatorPageEventRange) =>
        previous.start === current.start && previous.end === current.end));

    this.dataSource = new GridDataSource(
      apiDataPipeline,
      pagePipeline,
      sortPipeline);

    this._dataSubscription = this.dataSource.connect().subscribe(data => this.records = data);
  }

  ngOnDestroy() {
    this._dataSubscription.unsubscribe();
    this.records = [];
  }

  ngAfterViewInit() {
    this._publishNotification(new UserNotification('applyFilter', null));
  }

  private _initApiDataPipeline(): Observable<DisplayStaleDatedChecksInfo[]> {
    this._manualFilterUpdate = new Subject<void>();

    let lastFilters: StaleDatedCheckFilters = null;
    const sendManualFilters = function() {
       return of(lastFilters);
    };
    const manualFiltersUpdate =
      this._manualFilterUpdate.pipe(sendManualFilters);

    const filtersSource = merge(
      this._filtersService.getFilters(),
      manualFiltersUpdate
    );
    return filtersSource.pipe(
      filter((filters) => filters != null),
      tap(() => this._clearStatusNotification()),
      map((filters: StaleDatedCheckFilters) => {
        lastFilters = filters;
        if (!filters.isFormInvalid()) {
          return this._uiPaymentService
            .getStaleDatedChecksPayInfo(
              filters.durationType,
              filters.dashboardFilters.payGroups,
              filters.checkAmount,
              filters.employeeNumbers,
              filters.firstNames,
              filters.lastNames,
              filters.checkNo,
              1,
              this.maxRecordCount
            )
            .pipe(
              catchError(error => {
                this._handleError(error);
                return of([] as DisplayStaleDatedChecksInfo[]);
              })
            );
        } else {
          return of([] as DisplayStaleDatedChecksInfo[]);
        }
      }),
      concatMap((data) => data),
      map(staleCheckInfo => {
        staleCheckInfo = staleCheckInfo || [];
        if (staleCheckInfo.length >= this.maxRecordCount) {
          this._setStatusNotification('moreRecords', { maxRecords: this.maxRecordCount });
        }
        staleCheckInfo.forEach(x => x.displayName = this._translateService.instant(
          'EMPLOYEE_PAYS.EMPLOYEE_FULL_NAME', { firstName: x.firstName, lastName: x.lastName }));
        return staleCheckInfo;
      }),
      tap(data => this._setRawData(data)));
  }

  private _setRawData(value: DisplayStaleDatedChecksInfo[]) {
    this.rawData = value;
    this._rawDataTable = {};
    for (const data of this.rawData) {
      this._rawDataTable[data.id] = data;
    }
    this._resetSelection();
    if (this._paginator) {
      this._paginator.totalItems = this.dataCount;
    }
    if (this.dataCount === 0) {
      this._setStatusNotification('noMatchingRecords');
    }
    if (this._notification) {
      this._publishNotification(this._notification);
    }
    if (this.notificationBarElement && this.notificationBarElement.nativeElement) {
      const ancestorContainers = this._scrollDispatcher.getAncestorScrollContainers(this.notificationBarElement);
      ancestorContainers.forEach(s => s.scrollTo({ top: 0, left: 0 }));
    }
  }

  private _resetSelection() {
    this._selectedStaleCheckTable = {};
    if (this._selectedStaleCheckItemCount !== 0) {
      this._selectedStaleCheckItemCount = 0;
      this._handleSelectionChanged();
    }
  }

  private _handleSelectionChanged() {
    this._cachedSelection = null;
  }

  private _handleError(error: any) {
    this._setStatusNotification('internalServerError');
  }

  isPaySelected(data: DisplayStaleDatedChecksInfo): boolean {
    return this._selectedStaleCheckTable[data.id];
  }

  areAllPaysSelected(): boolean {
    return this.dataCount > 0 && this._selectedStaleCheckItemCount === this.dataCount;
  }

  areAnyPaysSelected(): boolean {
    return this._selectedStaleCheckItemCount > 0;
  }

  clickAllPays(selected: boolean) {
    // This is a work around for IE not supporting indeterminate checkbox state correctly.  If the checkbox is
    // clicked in indeterminate state we force it to select all to behave like other browsers.
    if (!this.areAllPaysSelected() && this.areAnyPaysSelected()) {
      this.selectAllPays(true);
    } else {
      this.selectAllPays(selected);
    }
  }

  get selectedPayItems(): DisplayStaleDatedChecksInfo[] {
    if (!this._cachedSelection) {
      const results: DisplayStaleDatedChecksInfo[] = [];
      for (const id in this._selectedStaleCheckTable) {
        if (this._selectedStaleCheckTable.hasOwnProperty(id) && this._selectedStaleCheckTable[id]) {
          const item = this._rawDataTable[id];
          if (item) {
            results.push(item);
          }
        }
      }

      this._cachedSelection = results;
    }

    return this._cachedSelection;
  }

  exportSelectedPays(): void {
    let items: DisplayStaleDatedChecksInfo[] = [];
    if (!GridDataSource.isEmptySort(this._sortSubject.value)) {
      items = this._sortSubject.value.isAsc
        ? _.orderBy(this.selectedPayItems, [this._sortSubject.value.key])
        : _.orderBy(this.selectedPayItems, [this._sortSubject.value.key], ['desc']);
    } else {
      items = this.selectedPayItems;
    }
    console.table(items);
    const exportedItems: ExportedPayItem[] = items.map(p =>
      new ExportedPayItem(
        p.lastName + ', ' + p.firstName,
        p.employeeNumber,
        p.payGroup,
        moment.utc(p.payUtcDate).format(ExportedPayItem.dateFormat),
        p.checkNumber,
        this._currencyPipe.transform(p.checkAmount.toString(), '1.02', undefined, true),
        moment.utc(p.staleDate).format(ExportedPayItem.dateFormat),
        this._translateService.instant(this.getStatusTextKey(p.status)),
        p.refundDate ? moment.utc(p.refundDate).format(ExportedPayItem.dateFormat) : '',
        this._translateService.instant(this.getRefundStatusTextKey(p.refundStatus)),
      ));
    this._fileExportService.exportAsCsvFile(
      exportedItems,
      'StaleChecks_' + this.settings.alias + '_' + moment().format('YYYYMMDDhhmmss'),
      ExportedPayItem.headers,
      ExportedPayItem.footer);
  }

  // Pagination event in Table
  public onPaginatorChanged(event: unknown) {
    this._pageSubject.next((event as CustomEvent<PaginatorPageEvent>).detail.range);
  }

  selectAllPays(selected: boolean) {
    if (!this.rawData) {
      return;
    }

    const oldCount = this._selectedStaleCheckItemCount;
    this._selectedStaleCheckItemCount = selected ? this.rawData.length : 0;

    if (this._selectedStaleCheckItemCount !== oldCount) {
      const payState = selected ? true : false;
      for (const data of this.rawData) {
        this._selectedStaleCheckTable[data.id] = payState;
      }

      this._handleSelectionChanged();
    }
  }

  clickPay(data: DisplayStaleDatedChecksInfo, selected: boolean) {
    this.selectPay(data, selected);
  }

  selectPay(data: DisplayStaleDatedChecksInfo, selected: boolean) {
    let selectedCount = 0;
    if (selected) {
      if (!this._selectedStaleCheckTable[data.id]) {
        this._selectedStaleCheckTable[data.id] = true;
        this._selectedStaleCheckItemCount++;
        selectedCount++;
        this._handleSelectionChanged();
      }
    } else {
      if (this._selectedStaleCheckTable[data.id]) {
        this._selectedStaleCheckTable[data.id] = false;
        this._selectedStaleCheckItemCount--;
        selectedCount--;
        this._handleSelectionChanged();
      }
    }
  }

  getStatusTextKey(status: PayCustomerItemStatus): string {
    return PayInfoFilters.getStatusTextKey(status);
  }

  getStatusDescriptionKey(status: PayCustomerItemStatus): string {
    return PayInfoFilters.getStatusDescriptionKey(status);
  }

  getRefundStatusTextKey(status: PayRefundStatus): string {
    return StaleDatedCheckFilters.getRefundStatusTextKey(status);
  }

  getRefundStatusDescriptionKey(status: PayRefundStatus): string {
    return StaleDatedCheckFilters.getRefundStatusDescriptionKey(status);
  }
}
